import { List } from "immutable";
import { clone, concat, filter, find, forEach, reduce } from "lodash";

import { immutableRecords } from "../../../types";
import rectUtils from "../../../utils/rectUtils";
import { getLayoutConstants } from "./getLayoutConstants";

const createBubbleWithRect = rect => {
  return {
    bounds: clone(rect),
    rects: [clone(rect)]
  };
};

const updateBubbleWithRect = (bubble, rect) => {
  return {
    bounds: rectUtils.rectUnion(bubble.bounds, rect),
    rects: concat(bubble.rects, rect).sort(sortRectByTopLeft)
  };
};

const updateBubbleWithBubble = (bubble1, bubble2) => {
  return {
    bounds: rectUtils.rectUnion(bubble1.bounds, bubble2.bounds),
    rects: concat(bubble1.rects, bubble2.rects).sort(sortRectByTopLeft)
  };
};

const sortRectByTopLeft = (rectA, rectB) => {
  if (rectA.top == rectB.top) {
    return rectA.left - rectB.left;
  }
  return rectA.top - rectB.top;
};

const sortByTopLeft = positions => (a, b) => {
  return sortRectByTopLeft(positions[a].totalRect, positions[b].totalRect);
};

const updateRectTop = (rect, top) =>
  rect.merge({
    top,
    bottom: top + rect.height
  });

const quantizeToLine = (top, lineHeight) => Math.ceil(top / lineHeight) * lineHeight;

/**
 *
 * @param {string} viewId
 * @param {object} results
 * @param {function} layoutFunc
 * @param {List} sortedEventIds
 * @param {Map} eventsById
 * @param {Set} ongoingDescendantsSet
 * @param {List} layoutIds
 * @param {Set} expandedItemsSet
 * @param {number} unitScale
 * @param {number} startY
 * @param {number} fontSize
 * @param {DateRecord} dateForNow
 * @param {boolean} showItemCard
 */
export const expandLayoutSecondPass = (
  viewId,
  results,
  layoutFunc,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  layoutIds,
  expandedItemsSet,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);
  const groupIdsSet = sortedEventIds.toSet();

  let positions = Object.assign({}, results.positions);
  let mostLeft = results.bounds.left;
  let mostRight = results.bounds.right;
  let mostBottom = results.bounds.bottom;

  // First: We need to sort the nodes in layoutIds by TOP and then LEFT
  // This is so that when an event is expanded, we are yet to visit all the items
  // below it that might be impacted
  const topDownLayoutIds = layoutIds.sort(sortByTopLeft(positions));

  // We use "bubbles" to track which items might need to be pushed down
  // to avoid conflicts with expanding parents.
  // We create a bubble for an expanded parent (unless it fits within an existing one)
  // This bubble tracks all of the items that have moved as a result of that expansion
  // For each item, we check if it overlaps with any of these expanding bubbles (or the rects
  // within those bubbles).
  // If it does overlap, we push it down further to avoid the collision, and add *that* new
  // position into the bubble also, since it may have a knock on effect for other items
  //
  // Only once we know an items final position, do we then calculate where to put all of its
  // children.
  let bubbles = [];

  // Work through the events in TOP -> LEFT order, as sorted above
  topDownLayoutIds.forEach(nodeId => {
    const item = eventsById.get(nodeId);
    let position = positions[nodeId];

    let selfAndChildRect = position.totalRect;

    const isExpanded = expandedItemsSet.has(nodeId);
    const childIds = isExpanded ? item.children.filter(id => groupIdsSet.has(id)) : List();
    let childPositions = {};

    // First, we need to work out if this item has children and is expanded, and if so,
    // calculate its new position.
    // We have to do this first so that we are checking for overlaps in its final state rather
    // than its unexpanded state (otherwise we may miss certain conditions)
    // If this item is expanded and has children
    if (isExpanded && childIds.size > 0) {
      const childrenStartY =
        quantizeToLine(position.totalRect.bottom, layoutConstants.LAYOUT_LINE_HEIGHT) +
        layoutConstants.EVENT_RECT_MARGIN;

      // Layout the children using the same top-level algorithm
      const childrenResult = layoutFunc(
        viewId,
        sortedEventIds,
        eventsById,
        ongoingDescendantsSet,
        childIds,
        expandedItemsSet,
        unitScale,
        childrenStartY,
        fontSize,
        dateForNow,
        showItemCard
      );

      childPositions = childrenResult.positions;

      // Calculate the new bounds for the children border
      const cTop = position.totalRect.top + layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT / 2;
      const cBottom = childrenResult.bounds.bottom + layoutConstants.CHILDREN_BORDER_BOTTOM_PADDING;
      const cLeft =
        Math.min(childrenResult.bounds.left, position.totalRect.left) - layoutConstants.CHILDREN_BORDER_LEFT_PADDING;
      const cRight =
        Math.max(childrenResult.bounds.right, position.totalRect.right) + layoutConstants.CHILDREN_BORDER_RIGHT_PADDING;

      const pBottom = cBottom + layoutConstants.CHILDREN_BORDER_BOTTOM_MARGIN;

      selfAndChildRect = position.totalRect.merge({
        bottom: pBottom,
        height: pBottom - position.totalRect.top,
        right: cRight,
        width: cRight - cLeft
      });

      // Set the children border and updated bottom on this parent
      position = position.set(
        "childrenBorderRect",
        immutableRecords.FlatRectRecord({
          left: cLeft,
          top: cTop,
          width: cRight - cLeft,
          height: cBottom - cTop,
          right: cRight,
          bottom: cBottom
        })
      );
    }

    // Now that we know the true size of this node, we need to see which bubbles it will overlap with

    // First, we need to work out if this event overlaps with any of the existing
    // expanding bubbles, and therefore needs to be moved itself

    // We need to find all the bubbles it overlaps with
    const matchingIndexes = reduce(
      bubbles,
      (result, bubble, index) => {
        const matchFound =
          rectUtils.rectOverlapsRect(bubble.bounds, selfAndChildRect) &&
          find(bubble.rects, r => rectUtils.rectOverlapsRect(r, selfAndChildRect));
        if (matchFound) {
          result.push(index);
        }
        return result;
      },
      []
    );

    // Grab the first index, this will be the one we use
    const bubbleIndex = matchingIndexes.length ? matchingIndexes[0] : -1;

    // But if there was more than one, we need to merge these bubbles together
    if (matchingIndexes.length > 1) {
      forEach(matchingIndexes, index => {
        if (index !== bubbleIndex) {
          // merge this bubble into our main one
          bubbles[bubbleIndex] = updateBubbleWithBubble(bubbles[bubbleIndex], bubbles[index]);
          // kill the merged bubble
          bubbles[index] = null;
        }
      });
      // and now filter out any of the nulls we inserted above
      bubbles = filter(bubbles, b => b);
    }

    // If we found a bubble that it clashes with, adjust the top of the position down until
    // it is safe.
    // Once we work out how far to adjust the parent, we also have to adjust the children by the
    // same amount.
    if (bubbleIndex >= 0) {
      // Initially, we will just adjust selfAndChildRect since that is the rect
      // we will be using for all our collision tests
      const initialTop = selfAndChildRect.top;
      bubbles[bubbleIndex].rects.forEach(r => {
        if (rectUtils.rectOverlapsRect(r, selfAndChildRect)) {
          const newTop = quantizeToLine(
            r.bottom + layoutConstants.EVENT_RECT_MARGIN,
            layoutConstants.LAYOUT_LINE_HEIGHT
          );
          selfAndChildRect = updateRectTop(selfAndChildRect, newTop);
        }
      });

      // Calculate how much it moved by
      const yOffset = selfAndChildRect.top - initialTop;
      // And if it moved at all, adjust the actual item positions and
      // all the children by the same amount
      // (this should also adjust grandchild descendants etc)
      if (yOffset !== 0) {
        position = position.update("totalRect", rect => updateRectTop(rect, rect.top + yOffset));
        position = position.update("childrenBorderRect", rect => updateRectTop(rect, rect.top + yOffset));
        forEach(childPositions, (childPos, childId) => {
          childPositions[childId] = childPos
            .update("totalRect", rect => updateRectTop(rect, rect.top + yOffset))
            .update("childrenBorderRect", rect => updateRectTop(rect, rect.top + yOffset));
        });
      }

      // Now add this node into the bubble, since we now know its final position and size
      bubbles[bubbleIndex] = updateBubbleWithRect(bubbles[bubbleIndex], selfAndChildRect);
    } else {
      // Or... if we are not part of an existing bubble, create a new one
      bubbles.push(createBubbleWithRect(selfAndChildRect.toObject()));
    }

    // Update our bounds
    if (mostLeft === null || selfAndChildRect.left < mostLeft) {
      mostLeft = selfAndChildRect.left;
    }
    if (mostRight === null || selfAndChildRect.right > mostRight) {
      mostRight = selfAndChildRect.right;
    }
    if (mostBottom === null || selfAndChildRect.bottom > mostBottom) {
      mostBottom = selfAndChildRect.bottom;
    }

    // Save the position back into the positions list
    positions[nodeId] = position;

    // Add the children positions into our big list of positions
    positions = Object.assign(positions, childPositions);
  });

  return {
    positions,
    bounds: immutableRecords.FlatRectRecord({
      left: mostLeft,
      right: mostRight,
      top: startY,
      bottom: mostBottom,
      width: mostRight - mostLeft,
      height: mostBottom - startY
    })
  };
};
